import { Action } from 'redux';
import { ThunkAction } from 'redux-thunk';
import { InterfaceTheme } from '@robocorp/robocloud-internal-cds-sdk/lib/src/types/RcInstanceDB';
import RcInstanceDTO from '@robocorp/robocloud-internal-cds-sdk/lib/sdk/Types';
import { ApiResponse } from 'apisauce';
import { OkAction, ErrorAction, Editing } from './baseActions';
import { userService, slackService } from '../services/userAPI';
import { workspaceService } from '../services/workspaceAPI';
import { formatApiErrorPayload } from './utils';

type AppState = import('~/store').AppState;

export enum UserActionType {
  GET_CURRENT_USER = 'GET_CURRENT_USER',
  GET_CURRENT_USER_OK = 'GET_CURRENT_USER_OK',
  GET_CURRENT_USER_ERROR = 'GET_CURRENT_USER_ERROR',
  EDIT_CURRENT_USER = 'EDIT_CURRENT_USER',
  EDIT_CURRENT_USER_OK = 'EDIT_CURRENT_USER_OK',
  EDIT_CURRENT_USER_ERROR = 'EDIT_CURRENT_USER_ERROR',
  UPDATE_CURRENT_USER_DETAILS = 'UPDATE_CURRENT_USER_DETAILS',
  UPDATE_CURRENT_USER_DETAILS_OK = 'UPDATE_CURRENT_USER_DETAILS_OK',
  UPDATE_CURRENT_USER_DETAILS_ERROR = 'UPDATE_CURRENT_USER_DETAILS_ERROR',
  DELETE_CURRENT_USER = 'DELETE_CURRENT_USER',
  DELETE_CURRENT_USER_OK = 'DELETE_CURRENT_USER_OK',
  DELETE_CURRENT_USER_ERROR = 'DELETE_CURRENT_USER_ERROR',
  DELETE_PERSONAL_WORKSPACE_DATA = 'DELETE_PERSONAL_WORKSPACE_DATA',
  DELETE_PERSONAL_WORKSPACE_DATA_OK = 'DELETE_PERSONAL_WORKSPACE_DATA_OK',
  DELETE_PERSONAL_WORKSPACE_DATA_ERROR = 'DELETE_PERSONAL_WORKSPACE_DATA_ERROR',
  ADD_HISTORY_EVENT = 'ADD_HISTORY_EVENT',
  REQUEST_SLACK_INVITE = 'REQUEST_SLACK_INVITE',
  REQUEST_SLACK_INVITE_OK = 'REQUEST_SLACK_INVITE_OK',
  REQUEST_SLACK_INVITE_ERROR = 'REQUEST_SLACK_INVITE_ERROR',
}

type WithEvent = { event: string };

export type CurrentUserEditingDetails =
  | Editing<'changingFirstName', 'editFirstNameError'>
  | Editing<'changingLastName', 'editLastNameError'>
  | Editing<'changingCountry', 'editCountryError'>
  | Editing<'changingCompanyName', 'editCompanyNameError'>
  | Editing<'changingEmail', 'editEmailError'>
  | Editing<'changingLanguage', 'editLanguageError'>
  | Editing<'changingTheme', 'editThemeError'>
  | Editing<'changingPicture', 'editPictureError'>
  | Editing<'changingInviteDismissed', 'changeInviteDismissed'>
  | Editing<'changingInstructionStep', 'changeInstructionStep'>;

export type CurrentUserUpdateDetails = Editing<
  'dismissingGettingStarted',
  'dismissingGettingStartedError'
>;

type GetCurrentUserAction =
  | Action<UserActionType.GET_CURRENT_USER>
  | OkAction<UserActionType.GET_CURRENT_USER_OK, RcInstanceDTO.User>
  | ErrorAction<UserActionType.GET_CURRENT_USER_ERROR>;

type EditCurrentUserAction =
  | (Action<UserActionType.EDIT_CURRENT_USER> & CurrentUserEditingDetails)
  | (OkAction<UserActionType.EDIT_CURRENT_USER_OK, RcInstanceDTO.User> & CurrentUserEditingDetails)
  | (ErrorAction<UserActionType.EDIT_CURRENT_USER_ERROR> & CurrentUserEditingDetails);

type UpdateCurrentUserDetailsAction =
  | (Action<UserActionType.UPDATE_CURRENT_USER_DETAILS> & CurrentUserUpdateDetails)
  | (OkAction<UserActionType.UPDATE_CURRENT_USER_DETAILS_OK, RcInstanceDTO.UserDetails> &
      CurrentUserUpdateDetails)
  | (ErrorAction<UserActionType.UPDATE_CURRENT_USER_DETAILS_ERROR> & CurrentUserUpdateDetails);

type DeleteCurrentUserAction =
  | Action<UserActionType.DELETE_CURRENT_USER>
  | OkAction<UserActionType.DELETE_CURRENT_USER_OK, number>
  | ErrorAction<UserActionType.DELETE_CURRENT_USER_ERROR>;

type DeletePersonalWorkspaceDataAction =
  | Action<UserActionType.DELETE_PERSONAL_WORKSPACE_DATA>
  | OkAction<UserActionType.DELETE_PERSONAL_WORKSPACE_DATA_OK, RcInstanceDTO.Workspace>
  | ErrorAction<UserActionType.DELETE_PERSONAL_WORKSPACE_DATA_ERROR>;

type AddHistoryEventAction = Action<UserActionType.ADD_HISTORY_EVENT> & WithEvent;

type RequestSlackInviteAction =
  | Action<UserActionType.REQUEST_SLACK_INVITE>
  | OkAction<UserActionType.REQUEST_SLACK_INVITE_OK, RcInstanceDTO.User>
  | ErrorAction<UserActionType.REQUEST_SLACK_INVITE_ERROR>;

export type UserAction =
  | GetCurrentUserAction
  | EditCurrentUserAction
  | UpdateCurrentUserDetailsAction
  | DeleteCurrentUserAction
  | DeletePersonalWorkspaceDataAction
  | AddHistoryEventAction
  | RequestSlackInviteAction;

export const getCurrentUser =
  (
    login?: boolean,
  ): ThunkAction<Promise<ApiResponse<RcInstanceDTO.User>>, AppState, null, GetCurrentUserAction> =>
  async (dispatch) => {
    dispatch({ type: UserActionType.GET_CURRENT_USER });

    const response = await userService.getCurrentUser(login);
    if (response.ok) {
      dispatch({
        type: UserActionType.GET_CURRENT_USER_OK,
        data: response.data as RcInstanceDTO.User,
      });
    } else {
      dispatch({
        ...formatApiErrorPayload(response),
        type: UserActionType.GET_CURRENT_USER_ERROR,
      });
    }

    return response;
  };

export const deleteCurrentUser =
  (): ThunkAction<Promise<ApiResponse<number>>, AppState, null, DeleteCurrentUserAction> =>
  async (dispatch) => {
    dispatch({ type: UserActionType.DELETE_CURRENT_USER });

    const response = await userService.deleteCurrentUser();
    if (response.ok) {
      dispatch({
        type: UserActionType.DELETE_CURRENT_USER_OK,
        data: response.data as number,
      });
    } else {
      dispatch({
        ...formatApiErrorPayload(response),
        type: UserActionType.DELETE_CURRENT_USER_ERROR,
      });
    }

    return response;
  };

export const deletePersonalWorkspaceData =
  (
    userId: string,
  ): ThunkAction<
    Promise<ApiResponse<RcInstanceDTO.Workspace>>,
    AppState,
    null,
    DeletePersonalWorkspaceDataAction
  > =>
  async (dispatch) => {
    dispatch({ type: UserActionType.DELETE_PERSONAL_WORKSPACE_DATA });

    const response = await workspaceService.deleteWorkspaceData(userId);
    if (response.ok) {
      dispatch({
        type: UserActionType.DELETE_PERSONAL_WORKSPACE_DATA_OK,
        data: response.data as RcInstanceDTO.Workspace,
      });
    } else {
      dispatch({
        ...formatApiErrorPayload(response),
        type: UserActionType.DELETE_PERSONAL_WORKSPACE_DATA_ERROR,
      });
    }

    return response;
  };

export const requestSlackInvite =
  (): ThunkAction<void, AppState, null, RequestSlackInviteAction> => async (dispatch) => {
    dispatch({ type: UserActionType.REQUEST_SLACK_INVITE });

    const response = await slackService.requestSlackInvite();
    if (response.ok && response.data) {
      const updateUserResponse = await userService.editCurrentUserDetails(response.data.details);
      if (updateUserResponse.ok) {
        dispatch({
          type: UserActionType.REQUEST_SLACK_INVITE_OK,
          data: response.data,
        });
      } else {
        dispatch({
          type: UserActionType.REQUEST_SLACK_INVITE_ERROR,
          error: (response.problem || updateUserResponse.problem) ?? 'UNKNOWN_ERROR',
        });
      }
    }
  };

const editCurrentUserDetails =
  (
    update: Partial<RcInstanceDTO.UserDetails>,
    editingDetails: CurrentUserEditingDetails,
  ): ThunkAction<void, AppState, null, EditCurrentUserAction> =>
  async (dispatch, getState) => {
    const currentUser = getState().userResources.users.current;
    const updated = { ...currentUser.details, ...update };
    dispatch({
      ...editingDetails,
      type: UserActionType.EDIT_CURRENT_USER,
    });

    const response = await userService.editCurrentUserDetails(updated);
    if (response.ok) {
      dispatch({
        ...editingDetails,
        type: UserActionType.EDIT_CURRENT_USER_OK,
        data: response.data as RcInstanceDTO.User,
      });
    } else {
      dispatch({
        ...formatApiErrorPayload(response),
        ...editingDetails,
        type: UserActionType.EDIT_CURRENT_USER_ERROR,
      });
    }
  };

/**
 * Similar to editCurrentUserDetails except two key differences
 * 1. uses patch requests so partial payload can be provided
 * 2. uses dashboard-api + CDS (id isn't used for SSO users)
 */
const updateCurrentUserDetails =
  (
    update: Partial<RcInstanceDTO.UserDetails>,
    editingDetails: CurrentUserUpdateDetails,
  ): ThunkAction<void, AppState, null, UpdateCurrentUserDetailsAction> =>
  async (dispatch) => {
    dispatch({
      ...editingDetails,
      type: UserActionType.UPDATE_CURRENT_USER_DETAILS,
    });
    const response = await userService.updateUserDetails(update);
    if (response.ok) {
      dispatch({
        ...editingDetails,
        type: UserActionType.UPDATE_CURRENT_USER_DETAILS_OK,
        data: response.data as RcInstanceDTO.UserDetails,
      });
    } else {
      dispatch({
        ...editingDetails,
        ...formatApiErrorPayload(response),
        type: UserActionType.UPDATE_CURRENT_USER_DETAILS_ERROR,
      });
    }
  };

export const dismissGettingStarted = (timestamp: string) =>
  updateCurrentUserDetails(
    { getting_started_dismissed_ts: timestamp },
    { statusKey: 'dismissingGettingStarted', errorKey: 'dismissingGettingStartedError' },
  );

export const changeCurrentUserFirstName = (firstName: string) =>
  editCurrentUserDetails(
    { first_name: firstName },
    { statusKey: 'changingFirstName', errorKey: 'editFirstNameError' },
  );

export const changeCurrentUserLastName = (lastName: string) =>
  editCurrentUserDetails(
    { last_name: lastName },
    { statusKey: 'changingLastName', errorKey: 'editLastNameError' },
  );

export const changeCurrentUserCompanyName = (companyName: string) =>
  editCurrentUserDetails(
    { company_name: companyName },
    { statusKey: 'changingCompanyName', errorKey: 'editCompanyNameError' },
  );

export const changeCurrentUserCountry = (country: string) =>
  editCurrentUserDetails(
    { country },
    { statusKey: 'changingCountry', errorKey: 'editCountryError' },
  );

export const changeCurrentUserEmail = (email: string) =>
  editCurrentUserDetails({ email }, { statusKey: 'changingEmail', errorKey: 'editEmailError' });

export const changeCurrentUserLanguage = (language: string) =>
  editCurrentUserDetails(
    { language },
    { statusKey: 'changingLanguage', errorKey: 'editLanguageError' },
  );

export const changeCurrentUserTheme = (theme: InterfaceTheme) =>
  editCurrentUserDetails({ theme }, { statusKey: 'changingTheme', errorKey: 'editThemeError' });

export const changeCurrentUserPicture = (picture: string) =>
  editCurrentUserDetails(
    { picture },
    { statusKey: 'changingPicture', errorKey: 'editPictureError' },
  );

export const changeCurrentUserSlackInviteDismissed = (inviteDismissed: string) =>
  editCurrentUserDetails(
    { slack_invite_dismissed_ts: inviteDismissed },
    { statusKey: 'changingInviteDismissed', errorKey: 'changeInviteDismissed' },
  );

export const changeCurrentUserInstructionStep = (step: number) =>
  editCurrentUserDetails(
    { instructions_step: step },
    { statusKey: 'changingInstructionStep', errorKey: 'changeInstructionStep' },
  );

export const addHistoryEvent =
  (event: string): ThunkAction<void, AppState, null, AddHistoryEventAction> =>
  (dispatch) => {
    dispatch({ type: UserActionType.ADD_HISTORY_EVENT, event });
  };
